import { apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import { normalizeApiError } from '../api/apiError';
import type {
  FaceOperationalStatus,
  FaceProviderOperationalStatus,
} from '../types/faceAttendance';
import type {
  FacePrivacyCleanupResult,
  FaceSyncProblem,
  PersonGroupInfo,
  PersonGroupEnsureResult,
  PersonGroupTrainAndWaitResult,
  AwsProviderEnsureResult,
} from '../types/faceAdmin';

type Envelope<T> = { success: boolean; message: string; data: T };
type RecordValue = Record<string, unknown>;

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function textOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function boolOrNull(value: unknown): boolean | null {
  if (value === true || value === 1 || value === '1') return true;
  if (value === false || value === 0 || value === '0') return false;
  return null;
}

function normalizeProvider(row: RecordValue | null | undefined): FaceProviderOperationalStatus {
  const data = row ?? {};
  return {
    enabled: boolOrNull(data.enabled),
    connected: boolOrNull(data.connected),
    status: textOrNull(data.status),
    message: textOrNull(data.message),
  };
}

function normalizePersonGroup(row: RecordValue | null | undefined): PersonGroupInfo | null {
  if (!row) return null;
  return {
    personGroupId: numberOrNull(row.personGroupId ?? row.biometricPersonGroupId),
    azurePersonGroupId: textOrNull(row.azurePersonGroupId ?? row.providerResourceId),
    groupStatus: textOrNull(row.groupStatus),
    trainingStatus: textOrNull(row.trainingStatus),
    syncStatus: textOrNull(row.syncStatus),
    biometricProvider: textOrNull(row.biometricProvider ?? row.providerCode),
  };
}

export async function getFaceOperationalStatus(): Promise<FaceOperationalStatus> {
  const response = await apiClient.get<Envelope<RecordValue>>(
    API_ENDPOINTS.FACE.OPERATIONAL_STATUS,
  );
  const data = response.data.data ?? {};
  return {
    azureFace: normalizeProvider(data.azureFace as RecordValue | undefined),
    awsRekognition: normalizeProvider(data.awsRekognition as RecordValue | undefined),
  };
}

export async function getFaceSyncProblems(): Promise<FaceSyncProblem[]> {
  const response = await apiClient.get<Envelope<RecordValue[] | { items?: RecordValue[] }>>(
    API_ENDPOINTS.FACE.SYNC_PROBLEMS,
  );
  const raw = response.data.data;
  const rows = Array.isArray(raw)
    ? raw
    : Array.isArray((raw as { items?: RecordValue[] })?.items)
      ? ((raw as { items: RecordValue[] }).items)
      : [];
  return rows.map(row => ({
    entityType: textOrNull(row.entityType),
    entityId: numberOrNull(row.entityId),
    employeeId: numberOrNull(row.employeeId),
    providerCode: textOrNull(row.providerCode),
    syncStatus: textOrNull(row.syncStatus),
    errorCode: textOrNull(row.errorCode),
    updatedAt: textOrNull(row.updatedAt),
  }));
}

export async function runFacePrivacyCleanup(): Promise<FacePrivacyCleanupResult> {
  const response = await apiClient.post<Envelope<RecordValue>>(
    API_ENDPOINTS.FACE.PRIVACY_CLEANUP,
    {},
  );
  const data = response.data.data ?? {};
  const syncErrors = (data.syncErrorsCleared as RecordValue) || {};
  const deletedIds = (data.deletedAzureIdsCleared as RecordValue) || null;
  return {
    verificationLogsSanitized: numberOrNull(data.verificationLogsSanitized) ?? 0,
    livenessSessionsSanitized: numberOrNull(data.livenessSessionsSanitized) ?? 0,
    syncErrorsCleared: {
      personGroups: numberOrNull(syncErrors.personGroups) ?? 0,
      registrations: numberOrNull(syncErrors.registrations) ?? 0,
      faces: numberOrNull(syncErrors.faces) ?? 0,
    },
    deletedAzureIdsCleared: deletedIds
      ? {
          registrations: numberOrNull(deletedIds.registrations) ?? 0,
          faces: numberOrNull(deletedIds.faces) ?? 0,
        }
      : null,
  };
}

export async function ensureOrganizationAwsProvider(): Promise<AwsProviderEnsureResult> {
  const response = await apiClient.post<
    Envelope<{ provider?: RecordValue; created?: boolean }>
  >(API_ENDPOINTS.FACE.ENSURE_AWS_PROVIDER, {});
  const data = response.data.data ?? {};
  const provider = data.provider ?? {};
  return {
    providerCode: textOrNull(provider.providerCode),
    providerResourceId: textOrNull(provider.providerResourceId),
    resourceStatus: textOrNull(provider.providerResourceStatus),
    syncStatus: textOrNull(provider.providerSyncStatus),
    created: data.created === true,
  };
}

export async function getOrganizationPersonGroup(): Promise<PersonGroupInfo | null> {
  try {
    const response = await apiClient.get<Envelope<{ personGroup?: RecordValue }>>(
      API_ENDPOINTS.FACE.PERSON_GROUP,
    );
    return normalizePersonGroup(response.data.data?.personGroup);
  } catch (error) {
    const apiError = normalizeApiError(error);
    if (
      apiError.code === 'AZURE_PERSON_GROUP_NOT_CONFIGURED' ||
      apiError.statusCode === 404
    ) {
      return null;
    }
    throw error;
  }
}

export async function ensureOrganizationPersonGroup(): Promise<PersonGroupEnsureResult> {
  const response = await apiClient.post<
    Envelope<{
      personGroup?: RecordValue;
      createdInAzure?: boolean;
      createdLocally?: boolean;
    }>
  >(API_ENDPOINTS.FACE.ENSURE_PERSON_GROUP, {});
  const data = response.data.data ?? {};
  return {
    personGroup: normalizePersonGroup(data.personGroup),
    createdInAzure: data.createdInAzure === true,
    createdLocally: data.createdLocally === true,
  };
}

const OPTIONAL_AZURE_GROUP_CODES = new Set([
  'AZURE_FACE_DISABLED',
  'AZURE_FACE_UNAVAILABLE',
  'AZURE_FACE_CONFIGURATION_ERROR',
  'AZURE_FACE_AUTHENTICATION_FAILED',
  'BIOMETRIC_PROVIDER_UNAVAILABLE',
]);

/** Azure Person Groups are optional when the org uses AWS Rekognition. */
export async function ensureOrganizationPersonGroupIfNeeded(): Promise<PersonGroupEnsureResult | null> {
  try {
    return await ensureOrganizationPersonGroup();
  } catch (error) {
    const apiError = normalizeApiError(error);
    if (OPTIONAL_AZURE_GROUP_CODES.has(apiError.code ?? '')) {
      return null;
    }
    throw error;
  }
}

export async function trainPersonGroupAndWait(): Promise<PersonGroupTrainAndWaitResult> {
  const response = await apiClient.post<Envelope<RecordValue>>(
    API_ENDPOINTS.FACE.TRAIN_PERSON_GROUP_AND_WAIT,
    {},
  );
  const data = response.data.data ?? {};
  return {
    personGroupId: numberOrNull(data.personGroupId),
    trainingStatus: textOrNull(data.trainingStatus) ?? 'SUCCEEDED',
    pollCount: numberOrNull(data.pollCount),
    durationMs: numberOrNull(data.durationMs),
  };
}
